import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Menu } from "@/components/ui/menu"
import {
  CalendarPlus,
  CalendarX,
  CircleCheck,
  Clock,
  Download,
  FileSpreadsheet,
  MessageSquare,
  MoreHorizontal,
  QrCode,
  Send,
  Table2,
  Trash2,
  UserRoundCheck,
  Users,
  XCircle,
} from "lucide-react"
import {
  CollectionView,
  type CollectionConfig,
} from "@/components/collection/CollectionView"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionLayout,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { GuestRecord } from "@/components/people/GuestRecord"
import { GuestImportWizard } from "@/components/people/GuestImportWizard"
import { MessageComposeDialog } from "@/components/people/MessageComposeDialog"
import { BulkInviteCodesSheet } from "@/components/people/BulkInviteCodesSheet"
import { BulkEditSheet } from "@/components/people/BulkEditSheet"
import { downloadMappedCsv } from "@/lib/csv"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  GUEST_LIFECYCLE,
  guestDisplayName,
  guestLifecycleActions,
  guestsQueryOptions,
  guestQueryOptions,
  invalidateGuestCaches,
  deleteGuestsBulk,
  runGuestLifecycle,
  runGuestLifecycleBulk,
  actionErrorMessage,
  joinName,
  rsvpBucket,
  statusLabel,
  displayStatus,
  STATUS_BADGE_CLS,
  type Guest,
  type GuestLifecycleSpec,
} from "@/core/people"
import { GuestCreatePane } from "@/components/people/GuestForm"
import { SessionRecord } from "@/components/schedule/SessionRecord"

// The People LAYOUT route. Route DEPTH = Main columns (mirrors forms.tsx):
//   /people             → col 1 (guest table)
//   /people/$guestId    → col 1 + 2 (table + GuestRecord); id "new" = create pane
// `$guestId` lives on a MARKER child route; this layout reads it off the leaf
// match and renders all columns via ColumnLayout (which decides how many fit).
// `?focus=2` fullscreens the record. Legacy `?guestId=…` URLs redirect into path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/people")({
  validateSearch: (
    s: Record<string, unknown>,
  ): { focus?: number; guestId?: string; session?: string; view?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      // Deep link into a saved view — the Overview funnel and stat cards land
      // the table on the lens they were counting.
      view: typeof s.view === "string" ? s.view : undefined,
      // Legacy — accepted only so beforeLoad can redirect it into the path.
      guestId: typeof s.guestId === "string" ? s.guestId : undefined,
      // Agenda item opened in the side rail from a guest's Schedule tab.
      session: typeof s.session === "string" ? s.session : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.guestId) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/people/$guestId",
        params: { ...params, guestId: search.guestId },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(guestsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: PeopleView,
})

const STATUS_BADGE = STATUS_BADGE_CLS

function PeopleView() {
  const { eventId } = useActiveEvent()
  const params = useParams({ from: "/$orgSlug/$eventSlug/people" })
  const { focus, session, view } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [msgTarget, setMsgTarget] = useState<{ guestId: string; name: string } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // Selected guests staged for the bulk invite-code / QR print sheet.
  const [bulkCodes, setBulkCodes] = useState<Guest[] | null>(null)
  // Selected guests staged for the spreadsheet-style bulk editor.
  const [bulkEdit, setBulkEdit] = useState<Guest[] | null>(null)

  const { guestId: selectedId } = useRouteRecordSelection("guestId")

  const guestsQ = useQuery({
    ...guestsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const guests = guestsQ.data ?? []

  // Nav: drilling into a guest CLEARS any fullscreen focus (you want to see what
  // you just opened, in context). ColumnLayout collapses to the record column
  // when the container is too narrow to show both.
  const openGuest = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/people/$guestId",
        params: { ...params, guestId: id },
        search: {},
      }),
    [navigate, params],
  )
  const openGuestFull = useCallback(
    (id: string) =>
      void navigate({
        to: "/$orgSlug/$eventSlug/people/$guestId",
        params: { ...params, guestId: id },
        search: { focus: 2 },
      }),
    [navigate, params],
  )
  const closeGuest = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/people", params, search: {} }),
    [navigate, params],
  )
  // Open / close an agenda item's record as a third column beside the guest
  // (driven by ?session=, so the guest record stays in view). Drops focus so
  // the columns sit side by side rather than fullscreening the session.
  const openSession = useCallback(
    (agendaItemId: string) => {
      if (!selectedId) return
      void navigate({
        to: "/$orgSlug/$eventSlug/people/$guestId",
        params: { ...params, guestId: selectedId },
        search: { session: agendaItemId },
      })
    },
    [navigate, params, selectedId],
  )
  const closeSession = useCallback(() => {
    if (!selectedId) return
    void navigate({
      to: "/$orgSlug/$eventSlug/people/$guestId",
      params: { ...params, guestId: selectedId },
      search: {},
    })
  }, [navigate, params, selectedId])
  // Fullscreen the record column (2). Toggles off if already focused.
  const toggleFocus = useCallback(
    (n: number) => {
      const next = focus === n ? undefined : n
      if (selectedId)
        void navigate({
          to: "/$orgSlug/$eventSlug/people/$guestId",
          params: { ...params, guestId: selectedId },
          search: { focus: next },
        })
      else void navigate({ to: "/$orgSlug/$eventSlug/people", params, search: { focus: next } })
    },
    [navigate, params, selectedId, focus],
  )

  // Per-row + bulk lifecycle actions go through the record endpoints with
  // optimistic cache patching (core/people). Same-resource → they mutate the
  // guest in place; the realtime layer + a follow-up invalidate reconcile.
  const runOne = useCallback(
    async (g: Guest, spec: GuestLifecycleSpec) => {
      try {
        await runGuestLifecycle(qc, g, spec)
        toast.success(spec.done(g))
      } catch (err) {
        toast.error(actionErrorMessage(err, `Couldn't ${spec.label(g).toLowerCase()}`))
      }
    },
    [qc],
  )

  const runBulk = useCallback(
    async (rows: Guest[], spec: GuestLifecycleSpec, verb: string) => {
      const applicable = rows.filter((g) => g.id && spec.enabled(g))
      if (applicable.length === 0) {
        toast.info(`No selected guests can be ${verb.toLowerCase()}.`)
        return
      }
      const { ok, failed, firstError } = await runGuestLifecycleBulk(qc, applicable, spec)
      invalidateGuestCaches(qc, eventId)
      if (failed === 0) toast.success(`${verb} ${ok} ${ok === 1 ? "guest" : "guests"}`)
      else
        toast.warning(
          `${verb} ${ok}; ${failed} failed${firstError ? ` — ${firstError}` : ""}`,
        )
    },
    [eventId, qc],
  )

  const messageGuest = useCallback(
    (g: Guest) => {
      // Message creates a TICKET (different resource) → opens the compose
      // dialog, intentionally NOT an inline same-resource mutation. Keyed on
      // the guest id, so guests without an account (Event Pass) are reachable.
      setMsgTarget({ guestId: g.id, name: guestDisplayName(g) })
    },
    [],
  )

  const config = useMemo<CollectionConfig<Guest>>(() => {
    const groupOf = (g: Guest) => (g.companyAtInvite?.trim() || "No company")
    const plusOnes = (g: Guest) => Math.max(0, g.maxGuests ?? 0)

    const exportCsv = (rows: Guest[]) => {
      downloadMappedCsv(
        "guests.csv",
        ["Name", "Email", "Status", "Job title", "Company", "Plus-ones", "Checked in"],
        rows,
        (g) => [
          guestDisplayName(g),
          g.emailAtInvite,
          g.status,
          g.titleAtInvite ?? "",
          g.companyAtInvite ?? "",
          plusOnes(g),
          g.checkedInAt ?? "",
        ],
      )
    }

    return {
      title: "People",
      getId: (g) => g.id,
      searchText: (g) =>
        `${guestDisplayName(g)} ${g.nameAtInvite} ${g.emailAtInvite} ${g.companyAtInvite ?? ""}`,
      onOpen: (g) => openGuest(g.id),
      onPrefetch: (g) => {
        void qc.prefetchQuery(guestQueryOptions(g.id)).catch(() => undefined)
      },
      // Right-click context menu: open / fullscreen / new-tab / new-window all
      // target the nested record column. Fullscreen forces the record column.
      getRecordHref: (g) => `/${params.orgSlug}/${params.eventSlug}/people/${g.id}`,
      onOpenFullscreen: (g) => openGuestFull(g.id),
      // Create form takes column 2 (the "new" sentinel id), same as a record.
      onNew: () => openGuest("new"),
      newLabel: "New guest",
      defaultSortId: "name",
      defaultSortDir: "asc",
      emptyTitle: "No guests yet",
      emptyBlurb: "Add the people you're inviting — they'll show up here as they RSVP.",
      emptyIcon: <Users className="h-5 w-5" />,
      getRecipient: (g) => ({ name: guestDisplayName(g), email: g.emailAtInvite }),
      savedViews: [
        { id: "all", label: "All", filter: () => true },
        { id: "confirmed", label: "Confirmed", filter: (g) => rsvpBucket(g.status) === "confirmed" },
        { id: "checked-in", label: "Checked in", filter: (g) => !!g.checkedInAt },
        { id: "pending", label: "Pending", filter: (g) => rsvpBucket(g.status) === "pending" },
        { id: "waitlist", label: "Waitlist", filter: (g) => g.status === "waitlisted" || g.status === "waitlist" },
        { id: "declined", label: "Declined", filter: (g) => rsvpBucket(g.status) === "declined" },
      ],
      defaultViewId: view,
      filters: [
        {
          id: "status",
          label: "Status",
          options: [
            ["all", "All"],
            ["confirmed", "Confirmed"],
            ["pending", "Pending"],
            ["declined", "Declined"],
          ],
          predicate: (v, g) => rsvpBucket(g.status) === v,
        },
        {
          id: "checkin",
          label: "Check-in",
          options: [
            ["all", "All"],
            ["in", "Checked in"],
            ["out", "Not checked in"],
          ],
          predicate: (v, g) => (v === "in" ? !!g.checkedInAt : !g.checkedInAt),
        },
        {
          id: "type",
          label: "Type",
          options: [
            ["all", "All"],
            ["primary", "Primary"],
            ["plus", "Plus-ones"],
          ],
          predicate: (v, g) => (v === "plus" ? !!g.hostGuestId : !g.hostGuestId),
        },
      ],
      groupOptions: [
        {
          id: "status",
          label: "Status",
          groupOf: (g) => statusLabel(g.status),
        },
        {
          id: "company",
          label: "Company",
          groupOf,
          order: (l) => (l === "No company" ? 99 : 0),
        },
      ],
      bulkActions: [
        {
          // Spreadsheet-style editor over the selected rows (guest columns +
          // attendee custom fields) — dryRun diff review before commit.
          id: "bulk-edit",
          label: "Bulk edit",
          icon: <Table2 className="h-4 w-4" />,
          run: (rows) => setBulkEdit(rows),
        },
        {
          id: "invite",
          // Lowest-common-action: offered only when the verb is the SAME for
          // every selected guest — all unsent (Send) or all previously sent
          // (Resend, status-preserving). A mixed selection hides it rather
          // than doing two different things in one click; filter to one
          // cohort first.
          label: (rows) =>
            rows[0]?.status === "draft" ? "Send invites" : "Resend invites",
          icon: <Send className="h-4 w-4" />,
          enabled: (rows) => {
            if (rows.length === 0) return false
            if (!rows.every((g) => GUEST_LIFECYCLE["send-invite"].enabled(g))) return false
            const drafts = rows.filter((g) => g.status === "draft").length
            return drafts === 0 || drafts === rows.length
          },
          run: (rows) => void runBulk(rows, GUEST_LIFECYCLE["send-invite"], "Invited"),
        },
        {
          // Registration-desk handout: mint a code/QR per selected guest and lay
          // them out as a printable sheet — no email sent. Same invitable
          // statuses as the per-guest "Invite code" action.
          id: "invite-codes",
          label: "Invite codes / QR sheet",
          icon: <QrCode className="h-4 w-4" />,
          enabled: (rows) =>
            rows.some((g) =>
              ["draft", "invited", "pending", "confirmed", "waitlisted"].includes(g.status),
            ),
          run: (rows) => {
            const applicable = rows.filter((g) =>
              g.id && ["draft", "invited", "pending", "confirmed", "waitlisted"].includes(g.status),
            )
            if (applicable.length === 0) {
              toast.info("None of the selected guests can be issued an invite code.")
              return
            }
            setBulkCodes(applicable)
          },
        },
        { id: "confirm", label: "Confirm", icon: <CircleCheck className="h-4 w-4" />, enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE.confirm.enabled(g)), run: (rows) => void runBulk(rows, GUEST_LIFECYCLE.confirm, "Confirmed") },
        {
          // Pending guests can't be "sent" an invite (no pending→invited
          // transition) — approve the seat and send the invite in one step.
          id: "approve-invite",
          label: "Approve & send invite",
          icon: <Send className="h-4 w-4" />,
          enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE["approve-invite"].enabled(g)),
          run: (rows) => void runBulk(rows, GUEST_LIFECYCLE["approve-invite"], "Approved & invited"),
        },
        { id: "approve", label: "Approve", icon: <CircleCheck className="h-4 w-4" />, enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE.approve.enabled(g)), run: (rows) => void runBulk(rows, GUEST_LIFECYCLE.approve, "Approved") },
        { id: "checkin", label: "Check in", icon: <UserRoundCheck className="h-4 w-4" />, enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE["check-in"].enabled(g)), run: (rows) => void runBulk(rows, GUEST_LIFECYCLE["check-in"], "Checked in") },
        { id: "send-calendar-invite", label: "Send calendar invite", icon: <CalendarPlus className="h-4 w-4" />, enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE["send-calendar-invite"].enabled(g)), run: (rows) => void runBulk(rows, GUEST_LIFECYCLE["send-calendar-invite"], "Calendar invite sent") },
        {
          id: "cancel-calendar-invite",
          label: "Cancel calendar invite",
          icon: <CalendarX className="h-4 w-4" />,
          danger: true,
          enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE["cancel-calendar-invite"].enabled(g)),
          run: (rows) => {
            const n = rows.filter((g) => GUEST_LIFECYCLE["cancel-calendar-invite"].enabled(g)).length
            if (n === 0) {
              toast.info("None of the selected guests have a calendar invite to cancel.")
              return
            }
            void runBulk(rows, GUEST_LIFECYCLE["cancel-calendar-invite"], "Calendar invite cancelled")
          },
        },
        {
          id: "cancel",
          label: "Cancel invites",
          icon: <XCircle className="h-4 w-4" />,
          danger: true,
          enabled: (rows) => rows.some((g) => GUEST_LIFECYCLE["cancel-invite"].enabled(g)),
          run: (rows) => {
            // Destructive → confirm with blast radius before committing. Count
            // only those the action can actually act on.
            const n = rows.filter((g) => GUEST_LIFECYCLE["cancel-invite"].enabled(g)).length
            if (n === 0) {
              toast.info("None of the selected guests have an invite to cancel.")
              return
            }
            if (confirm(`Cancel invites for ${n} ${n === 1 ? "guest" : "guests"}? They lose event access.`)) {
              void runBulk(rows, GUEST_LIFECYCLE["cancel-invite"], "Cancelled")
            }
          },
        },
        {
          id: "delete",
          label: "Delete",
          icon: <Trash2 className="h-4 w-4" />,
          danger: true,
          // Backend only deletes RESCINDED guests (cancel the invite first).
          enabled: (rows) => rows.some((g) => g.status === "rescinded"),
          run: (rows) => {
            const targets = rows.filter((g) => g.id && g.status === "rescinded")
            if (targets.length === 0) {
              toast.info("Only guests with a cancelled invite can be deleted.")
              return
            }
            const n = targets.length
            if (
              confirm(
                `Permanently delete ${n} ${n === 1 ? "guest" : "guests"}? This removes their record and can't be undone.`,
              )
            ) {
              void (async () => {
                const { ok, failed, firstError } = await deleteGuestsBulk(qc, targets)
                if (failed === 0)
                  toast.success(`Deleted ${ok} ${ok === 1 ? "guest" : "guests"}`)
                else
                  toast.warning(
                    `Deleted ${ok}; ${failed} failed${firstError ? ` — ${firstError}` : ""}`,
                  )
              })()
            }
          },
        },
        { id: "export", label: "Export", icon: <Download className="h-4 w-4" />, run: exportCsv },
      ],
      // Collection-level ⋮ menu — runs on the currently filtered + sorted rows
      // (no selection needed). Filter/sort in the action bar, then copy.
      menuActions: [
        {
          id: "import-guests",
          label: "Import guests",
          icon: <FileSpreadsheet className="h-4 w-4" />,
          run: () => setImportOpen(true),
        },
      ],
      columns: [
        {
          id: "name",
          header: "Name",
          sortable: true,
          sortValue: (g) => guestDisplayName(g).toLowerCase(),
          render: (g) => {
            const display = guestDisplayName(g)
            const legal = joinName(g.firstName, g.lastName)
            // Job title + company now have their own sortable columns, so the
            // name cell just carries the display name (+ legal name when it differs).
            return (
              <div className="flex min-w-0 flex-col">
                <span className="flex items-center gap-1.5 truncate font-semibold text-plum-900 dark:text-plum-50">
                  {display}
                  {g.hostGuestId && (
                    <span className="rounded bg-plum-100 px-1 text-[9px] font-bold text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55">
                      +1
                    </span>
                  )}
                </span>
                {legal && legal !== display && (
                  <span className="truncate text-[11px] text-plum-700/55 dark:text-plum-200/40">
                    {legal}
                  </span>
                )}
              </div>
            )
          },
        },
        {
          id: "email",
          header: "Email",
          className: "text-plum-700/70 dark:text-plum-200/55",
          render: (g) =>
            g.emailAtInvite ? (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  openGuest(g.id)
                }}
                className="truncate rounded text-left hover:text-plum-900 hover:underline dark:hover:text-plum-50"
              >
                {g.emailAtInvite}
              </button>
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          id: "status",
          header: "RSVP",
          sortable: true,
          sortValue: (g) => displayStatus(g),
          render: (g) => (
            <span
              className={`rounded-md px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_BADGE[displayStatus(g)] ?? "bg-plum-100 text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55"}`}
            >
              {statusLabel(displayStatus(g))}
            </span>
          ),
        },
        {
          id: "title",
          header: "Job title",
          className: "text-plum-700/70 dark:text-plum-200/55",
          sortable: true,
          sortValue: (g) => (g.titleAtInvite ?? "").toLowerCase(),
          render: (g) =>
            g.titleAtInvite?.trim() ? (
              g.titleAtInvite
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          id: "company",
          header: "Company",
          className: "text-plum-700/70 dark:text-plum-200/55",
          sortable: true,
          sortValue: (g) => (g.companyAtInvite ?? "").toLowerCase(),
          render: (g) =>
            g.companyAtInvite?.trim() ? (
              g.companyAtInvite
            ) : (
              <span className="text-plum-700/40 dark:text-plum-200/30">—</span>
            ),
        },
        {
          id: "plus",
          header: "+ guests",
          className: "text-plum-700/70 dark:text-plum-200/55 tabular-nums",
          sortable: true,
          sortValue: (g) => plusOnes(g),
          render: (g) => (plusOnes(g) > 0 ? plusOnes(g) : <span className="text-plum-700/35 dark:text-plum-200/25">0</span>),
        },
        {
          id: "preapproved",
          header: "Pre-approved",
          render: (g) =>
            g.preApproved === 1 ? (
              <span className="rounded bg-plum-100 px-1.5 py-0.5 text-[10px] font-bold text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55">
                Yes
              </span>
            ) : (
              <span className="text-plum-700/35 dark:text-plum-200/25">—</span>
            ),
        },
        {
          id: "checkin",
          header: "Checked in",
          sortable: true,
          sortValue: (g) => g.checkedInAt ?? "",
          render: (g) =>
            g.checkedInAt ? (
              <span className="inline-flex items-center gap-1 text-[12px] font-medium text-emerald-700 dark:text-emerald-400">
                <CircleCheck className="h-3.5 w-3.5" />
                {fmtTime(g.checkedInAt)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[12px] text-plum-700/40 dark:text-plum-200/30">
                <Clock className="h-3.5 w-3.5" />
                Not yet
              </span>
            ),
        },
        {
          id: "actions",
          header: "",
          className: "w-10 pr-3",
          // Per-row lifecycle menu. Same-resource actions mutate the guest in
          // place; "Message" creates a ticket (right-rail compose). stopProp so
          // clicking the trigger / items never opens the record.
          render: (g) => (
            <RowActions
              guest={g}
              onRun={(spec) => void runOne(g, spec)}
              onMessage={() => messageGuest(g)}
            />
          ),
        },
      ],
    }
  }, [openGuest, openGuestFull, params, qc, runOne, runBulk, messageGuest, view])

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [
      {
        id: "list",
        minWidth: 320,
        width: 380,
        node: (
          <CollectionView
            config={config}
            rows={guests}
            activeId={selectedId}
            loading={guestsQ.isLoading && guests.length === 0}
            error={
              guestsQ.isError
                ? ((guestsQ.error as Error)?.message ?? "Failed to load guests")
                : null
            }
          />
        ),
      },
    ]
    if (selectedId && eventId) {
      cols.push({
        id: "record",
        minWidth: 320,
        fill: true,
        node:
          selectedId === "new" ? (
            <GuestCreatePane
              eventId={eventId}
              onClose={closeGuest}
              // Jump to the new guest's record when the API echoes its id, else
              // just close back to the list.
              onCreated={(id) => (id ? openGuest(id) : closeGuest())}
            />
          ) : (
            <GuestRecord
              key={selectedId}
              guestId={selectedId}
              onClose={closeGuest}
              onFullscreen={() => toggleFocus(2)}
              onOpenSession={openSession}
            />
          ),
      })
    }
    // Third column: an agenda item opened from the guest's Schedule tab.
    if (selectedId && selectedId !== "new" && session && eventId) {
      cols.push({
        id: "session",
        minWidth: 340,
        fill: true,
        node: (
          <SessionRecord
            key={session}
            agendaItemId={session}
            onBack={closeSession}
            closeVariant="close"
          />
        ),
      })
    }
    return cols
  }, [
    config,
    guests,
    guestsQ.isLoading,
    guestsQ.isError,
    guestsQ.error,
    selectedId,
    eventId,
    closeGuest,
    openGuest,
    toggleFocus,
    openSession,
    closeSession,
    session,
  ])

  return (
    <RoutedCollectionLayout columns={columns} focus={focus} className="relative flex min-h-0 flex-1 flex-col">
      {msgTarget && (
        <MessageComposeDialog
          open
          onClose={() => setMsgTarget(null)}
          guestId={msgTarget.guestId}
          name={msgTarget.name}
        />
      )}
      <GuestImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        eventId={eventId}
      />
      {bulkCodes && (
        <BulkInviteCodesSheet rows={bulkCodes} onClose={() => setBulkCodes(null)} />
      )}
      {bulkEdit && eventId && (
        <BulkEditSheet eventId={eventId} rows={bulkEdit} onClose={() => setBulkEdit(null)} />
      )}
    </RoutedCollectionLayout>
  )
}

// Per-row overflow menu — status-gated lifecycle actions + Message. Rendered
// inside a CollectionView cell, so every interactive surface stops propagation
// to keep the row's open-on-click from firing underneath.
function RowActions({
  guest,
  onRun,
  onMessage,
}: {
  guest: Guest
  onRun: (spec: GuestLifecycleSpec) => void
  onMessage: () => void
}) {
  const actions = guestLifecycleActions(guest)
  return (
    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
      <Menu.Root>
        <Menu.Trigger
          className="grid h-7 w-7 place-items-center rounded-md text-plum-700/55 transition hover:bg-plum-100/70 hover:text-plum-700 dark:text-plum-200/45 dark:hover:bg-white/5"
          title="Actions"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner sideOffset={4} align="end" className="z-[60] outline-none">
            <Menu.Popup className="mb-pop min-w-[11rem]">
              <Menu.Item
                className="mb-mi"
                onClick={onMessage}
              >
                <MessageSquare className="mb-mii" />
                Message
              </Menu.Item>
              {actions.map((spec) => {
                const danger = spec.danger
                return (
                  <Menu.Item
                    key={spec.id}
                    className={danger ? "mb-mi mb-danger" : "mb-mi"}
                    onClick={() => {
                      if (
                        danger &&
                        !confirm(
                          `${spec.label(guest)} for ${guestDisplayName(guest)}? They lose event access.`,
                        )
                      )
                        return
                      onRun(spec)
                    }}
                  >
                    {ACTION_ICON[spec.id]}
                    {spec.label(guest)}
                  </Menu.Item>
                )
              })}
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </div>
  )
}

const ACTION_ICON: Record<GuestLifecycleSpec["id"], React.ReactNode> = {
  "send-invite": <Send className="mb-mii" />,
  confirm: <CircleCheck className="mb-mii" />,
  approve: <CircleCheck className="mb-mii" />,
  "approve-invite": <Send className="mb-mii" />,
  "check-in": <UserRoundCheck className="mb-mii" />,
  "check-out": <UserRoundCheck className="mb-mii" />,
  "cancel-check-in": <XCircle className="mb-mii" />,
  "cancel-check-out": <XCircle className="mb-mii" />,
  "cancel-invite": <XCircle className="mb-mii" />,
  "send-calendar-invite": <CalendarPlus className="mb-mii" />,
  "cancel-calendar-invite": <CalendarX className="mb-mii" />,
}

function fmtTime(iso: string): string {
  const t = Date.parse(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`)
  if (Number.isNaN(t)) return ""
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}
